import { Suspense, type ReactElement } from "react";
// Shared TYPE from the backend/foundation contract. This is a type-only import
// (enforced by the eslint web boundary rule: web may consume @avos/foundation
// for shared types only, never runtime values). It proves the shared-types
// wiring the frontend depends on: the shell can carry a backend error envelope
// through to a User_Message component (Req 26) without duplicating the shape.
import type { ErrorEnvelope } from "@avos/foundation";
import { matchRoute } from "./routes.js";
import { SERVER_SCREENS, CLIENT_SCREENS } from "../screens/screens.js";
import { ArchMark } from "../console/ArchMark.js";

/**
 * Which screen-loading strategy the shell uses:
 *   - `"server"` renders eager screens so `renderToString` emits real screen
 *     content (Req 21.1); this is the default.
 *   - `"client"` renders `React.lazy`, code-split screens for browser
 *     hydration (route-level code splitting).
 */
export type ShellMode = "server" | "client";

/** Props for the shell. */
export interface AppShellProps {
  /** The request pathname to render (drives navigation active-state + screen). */
  readonly url: string;
  /**
   * Screen-loading strategy. Defaults to `"server"` (eager) so a bare
   * `renderAppShell` call produces fully server-rendered screen content.
   */
  readonly mode?: ShellMode;
  /**
   * An optional server-provided error envelope to surface to the user. Typed
   * with the SHARED foundation contract so the frontend and backend agree on
   * the shape (later wired to the UserMessage component — Req 26). Optional per
   * `exactOptionalPropertyTypes`: omit the key entirely when there is no error.
   */
  readonly error?: ErrorEnvelope;
}

/**
 * The Application_Shell layout: a landmark-based skeleton (banner header with
 * the AVOS lockup + a main content region) into which each route's screen
 * renders. The public/auth shell no longer carries the old foundation
 * horizontal route navigation — auth screens present a clean, focused surface
 * and authenticated navigation lives in the app shell (sidebar). The layout is
 * still fully server-rendered (Req 21.1); the screen inside `<main>` is the
 * swappable, code-split content.
 */
export function AppShell({ url, mode = "server", error }: AppShellProps): ReactElement {
  const route = matchRoute(url);
  const Screen =
    mode === "client" ? CLIENT_SCREENS[route.screen] : SERVER_SCREENS[route.screen];
  return (
    <div data-app-shell="avos" data-active-route={route.path}>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <header role="banner">
        <span className="brand">
          <ArchMark size={22} baselineColor="#101828" />
          <span className="brand__name">AVOS</span>
        </span>
      </header>
      <main id="main-content" role="main" tabIndex={-1}>
        {error ? (
          <div role="alert" data-testid="shell-error">
            {error.error.message}
          </div>
        ) : null}
        {/*
          Screens are code-split (React.lazy). Suspense guarantees the shell's
          layout + navigation still render even if a screen's chunk has not
          resolved on the server — the Req 21.1 contract never depends on a
          screen being ready.
        */}
        <Suspense fallback={<div data-testid="screen-loading">Loading…</div>}>
          <Screen />
        </Suspense>
      </main>
    </div>
  );
}
